import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  makeStyles,
  Typography
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Grid from "@saleor/components/Grid";
import Hr from "@saleor/components/Hr";
import { BrandErrorFragment } from "@saleor/fragments/types/BrandErrorFragment";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import ImageIcon from "@saleor/icons/Image";
import { commonMessages } from "@saleor/intl";
import classNames from "classnames";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";

import { BrandGroupFormData } from "../BrandGroupDetailsPage";

const useStyles = makeStyles(
  theme => ({
    card: {
      marginTop: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        marginTop: 0
      }
    },
    descriptionLabel: {
      margin: "5px 0px 5px 0px"
    },
    fileField: {
      display: "none"
    },
    fileUploadField: {
      padding: "0!important"
    },
    guidelines: {
      fontWeight: "bold"
    },
    helpText: {
      gridColumnEnd: "span 4"
    },
    icon: {
      color: "rgba(255, 255, 255, 0.54)"
    },
    image: {
      height: "100%",
      objectFit: "contain",
      userSelect: "none",
      width: "100%"
    },
    imageContainer: {
      "&:hover, &.dragged": {
        "& $imageOverlay": {
          display: "block"
        }
      },
      background: "#ffffff",
      border: "1px solid #eaeaea",
      borderRadius: theme.spacing(),
      height: 140,
      margin: "auto",
      overflow: "hidden",
      position: "relative",
      width: 140
    },
    imageGridContainer: {
      position: "relative"
    },

    imageOverlay: {
      background: "rgba(0, 0, 0, 0.6)",
      cursor: "move",
      display: "none",
      height: "100%",
      left: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageOverlayShow: {
      "&$imageOverlay": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center"
      }
    },
    imageOverlayToolbar: {
      display: "flex",
      position: "absolute",
      right: 0,
      top: 0
    },
    imageUpload: {
      height: "100%",
      left: 0,
      outline: 0,
      position: "absolute",
      top: 0,
      width: "100%"
    },
    imageUploadActive: {
      zIndex: 1
    },
    imageUploadIconActive: {
      display: "block"
    },
    imageflex: {},
    info: {
      color: "#0937f4",
      fontSize: 17,
      fontWeight: "bold"
    },
    infoGuidelines: {
      fontSize: 13,
      marginLeft: "10px"
    },
    photosIcon: {
      height: "64px",
      margin: "0 auto",
      width: "64px"
    },
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(2),
      gridTemplateColumns: "repeat(4, 1fr)",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(3, 1fr)"
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, 1fr)"
      }
    },
    rootDragActive: {
      opacity: 0.2
    },
    uploadButton: {
      display: "block"
    },
    uploadRoot: {
      fontSize: 12,
      fontWeight: 600,
      padding: "15px",
      textAlign: "center"
    }
  }),
  { name: "StoreBannerImage" }
);

interface BrandGroupImageProps {
  data: BrandGroupFormData;
  errors: BrandErrorFragment[];
  disabled: boolean;
  loading?: boolean;
  onChange: FormChange;
  onImageDelete?: () => void;
  onImageUpload?: (file: File) => void;
  onFileSizeError: () => void;
  onIconUpload?: (file: File) => void;
}

const fileSizeLimit = 3.0e6;

const BrandGroupImage: React.FC<BrandGroupImageProps> = props => {
  const classes = useStyles(props);
  const intl = useIntl();
  const upload = React.useRef(null);
  const notify = useNotifier();
  const [iconToUpload, setIconToUpload] = React.useState(null);
  const [image, setImage] = React.useState(null);
  const { data, loading, onChange, onIconUpload } = props;

  React.useEffect(() => {
    if (iconToUpload) {
      setImage(URL.createObjectURL(iconToUpload[0]));
      onIconUpload(iconToUpload[0]);
    } else {
      onIconUpload(null);
    }
  }, [iconToUpload]);

  const checkFile = e => {
    const file = e[0];

    if (file.size < fileSizeLimit) {
      if (
        file.type.includes("image/jpeg") ||
        file.type.includes("image/jpg") ||
        file.type.includes("image/png")
      ) {
        setIconToUpload(e);
        onIconUpload(e);
        onChange({
          target: {
            name: "image",
            value: file
          }
        });
      } else {
        notify({
          status: "error",
          text:
            "The format is not acceptable. Check the format and upload again!"
        });
      }
    } else {
      notify({
        status: "error",
        text: "File size is too big! Try compressing.",
        title: "Can't Upload."
      });
    }
  };

  return (
    <Card className={classes.card}>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Brand Image",
          description: "section header"
        })}
        toolbar={
          <>
            <Button
              onClick={() => upload.current.click()}
              disabled={loading}
              variant="text"
              color="primary"
              data-test="button-upload-image"
              className={classes.uploadButton}
            >
              {intl.formatMessage(commonMessages.uploadImage)}
            </Button>
            <input
              className={classes.fileField}
              id="fileUpload"
              onChange={event => checkFile(event.target.files)}
              type="file"
              ref={upload}
              accept="image/jpeg, image/jpg, image/png"
            />
          </>
        }
      />
      <div
        className={classNames(classes.imageGridContainer, {
          [classes.imageflex]: iconToUpload || data.image?.url
        })}
      >
        <CardContent>
          <Dropzone onDrop={checkFile} disableClick={true}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <CardContent className={classes.fileUploadField}>
                  <input
                    {...getInputProps()}
                    id="fileUpload"
                    accept="image/jpeg, image/jpg, image/png"
                    onChange={e =>
                      e.target.files.length > 0 && checkFile(e.target.files)
                    }
                    multiple={false}
                    ref={upload}
                    type="file"
                  />
                  {iconToUpload || data.image?.url ? (
                    <div className={classes.imageContainer}>
                      <div
                        className={classNames(classes.imageOverlay, {
                          [classes.imageOverlayShow]: loading
                        })}
                      >
                        {loading ? (
                          <CircularProgress size={32} />
                        ) : (
                          <div className={classes.imageOverlayToolbar}>
                            <IconButton
                              color="primary"
                              onClick={() => {
                                upload.current.value = null;
                                setImage(null);
                                setIconToUpload(null);
                                onChange({
                                  target: {
                                    name: "image",
                                    value: null
                                  }
                                });
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </div>
                        )}
                      </div>
                      <img
                        className={classes.image}
                        src={iconToUpload ? image : data.image.url}
                      />
                    </div>
                  ) : (
                    <div className={classes.uploadRoot}>
                      <ImageIcon className={classes.photosIcon} />
                      <p className={classes.descriptionLabel}>
                        <FormattedMessage
                          defaultMessage="DROP IMAGE HERE TO UPLOAD"
                          description="description"
                        />
                      </p>
                    </div>
                  )}
                </CardContent>
              </div>
            )}
          </Dropzone>
        </CardContent>
        <Hr />
        <CardContent>
          <Grid>
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexWrap: "wrap"
              }}
            >
              <InfoOutlined color="primary" />
              <Typography className={classes.info}>
                {" "}
                Upload Guidelines:
              </Typography>
            </div>
          </Grid>
          <div>
            <span className={classes.infoGuidelines}>
              {" "}
              1. <span className={classes.guidelines}>Aspect Ratio:</span>{" "}
              Square logos need to have an aspect ratio of exactly 1:1.
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              2.<span className={classes.guidelines}>Dimension:</span> Image
              must be between 500x500 pixels and 800x800 pixels
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              3. <span className={classes.guidelines}>File type:</span> JPG,
              JPEG, or PNG file.
            </span>
            <br />
            <span className={classes.infoGuidelines}>
              {" "}
              4. <span className={classes.guidelines}>File size:</span> Image
              file can't be larger than 3MB.
            </span>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};
BrandGroupImage.displayName = "BrandGroupImage";
export default BrandGroupImage;
